import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { SofaModule, SerializedSofaModule } from './objects/SofaModule';
import { ProjectRenderer } from './ProjectRenderer';
import { UUID } from '@/types/types';
import { ConnectingSlot, SlotType } from './objects/ConnectingSlot';
import { AssetsLib } from './utils/AssetsLib';
import { ThreeEvent } from './types';
import { ProductModule } from '@/api/ProductModuleApi';

export type SerializedModulesManager = {
	sofaModules: SerializedSofaModule[];
};

export class ModulesManager {
	private assetsLib = AssetsLib.getInstance();
	sofaModulesMap = new Map<UUID, SofaModule>();

	constructor(private renderer: ProjectRenderer) {}

	private traverse(
		sofaModule: SofaModule,
		direction: SlotType,
		callback: (sofaModule: SofaModule) => void
	) {
		callback(sofaModule);

		const slot = sofaModule.connectingSlots.find(
			(s) => s.slotType === direction
		);
		if (!slot) return;
		if (!slot.connectedSlot) return;

		this.traverse(slot.connectedSlot.hostModule, direction, callback);
	}

	private getSlotByType(sofaModule: SofaModule, slotType: SlotType) {
		return sofaModule.connectingSlots.find(
			(slot) => slot.slotType === slotType
		);
	}

	private shiftRightChain(startModule: SofaModule) {
		let currentModule: SofaModule | null = startModule;

		while (currentModule) {
			const currentRightSlot = this.getSlotByType(
				currentModule,
				SlotType.RIGHT
			);
			if (!currentRightSlot) return;

			const nextSlot = currentRightSlot.connectedSlot;
			if (!nextSlot) return;

			const nextModule = nextSlot.hostModule;
			const nextLeftSlot = this.getSlotByType(nextModule, SlotType.LEFT);
			if (!nextLeftSlot) return;

			nextModule.alignToSlot(currentRightSlot, nextLeftSlot);
			currentModule = nextModule;
		}
	}

	setOrder() {
		let edgeRightModule = this.sofaModulesMap.values().next().value!;

		this.traverse(edgeRightModule, SlotType.RIGHT, (sofaModule) => {
			edgeRightModule = sofaModule;
		});

		const modulesOrder: UUID[] = [];

		this.traverse(edgeRightModule, SlotType.LEFT, (sofaModule) => {
			modulesOrder.push(sofaModule.id);
		});

		this.renderer.emitter.emit(
			ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER,
			modulesOrder
		);
	}

	async addSofaModule(creatorModule: CreatorProductModule, isFirst?: true) {
		const asset = creatorModule.productModule.asset;
		if (!asset.model) throw new Error('MISSING_MODEL');

		const model =
			this.assetsLib.models.get(asset.model.path) ||
			(await this.assetsLib.loadModel(asset.model.path));

		const sofaModule = new SofaModule(
			this.renderer,
			asset,
			creatorModule.id,
			model.clone(true)
		);
		sofaModule.createConnectingSlots();

		this.addModuleToCollection(sofaModule);

		if (isFirst) return;

		sofaModule.connectBySlot();
		this.setOrder();
	}

	async swapSofaModule(id: UUID, productModule: ProductModule) {
		const sofaModule = this.sofaModulesMap.get(id);
		if (!sofaModule) throw new Error('SOFA_MODULE_NOT_FOUND');

		const oldLeftSlot = this.getSlotByType(sofaModule, SlotType.LEFT);
		const oldRightSlot = this.getSlotByType(sofaModule, SlotType.RIGHT);
		const leftNeighborSlot = oldLeftSlot?.connectedSlot ?? null;
		const rightNeighborSlot = oldRightSlot?.connectedSlot ?? null;

		const asset = productModule.asset;
		if (!asset.model) throw new Error('MISSING_MODEL');

		const model =
			this.assetsLib.models.get(asset.model.path) ||
			(await this.assetsLib.loadModel(asset.model.path));

		sofaModule.swapModel(asset, model.clone(true));
		sofaModule.createConnectingSlots();

		const newLeftSlot = this.getSlotByType(sofaModule, SlotType.LEFT);
		const newRightSlot = this.getSlotByType(sofaModule, SlotType.RIGHT);

		if (leftNeighborSlot && newLeftSlot)
			newLeftSlot.connectTo(leftNeighborSlot);
		if (rightNeighborSlot && newRightSlot)
			newRightSlot.connectTo(rightNeighborSlot);

		const hasLeft = !!leftNeighborSlot && !!newLeftSlot;
		const hasRight = !!rightNeighborSlot && !!newRightSlot;

		if (!hasLeft && !hasRight) {
			this.setOrder();
			this.renderer.modulesChangeSideEffects();
			return;
		}

		if (hasLeft && !hasRight) {
			sofaModule.alignToSlot(leftNeighborSlot!, newLeftSlot!);
			this.setOrder();
			this.renderer.modulesChangeSideEffects();
			return;
		}

		if (!hasLeft && hasRight) {
			sofaModule.alignToSlot(rightNeighborSlot!, newRightSlot!);
			this.setOrder();
			this.renderer.modulesChangeSideEffects();
			return;
		}

		sofaModule.alignToSlot(leftNeighborSlot!, newLeftSlot!);
		this.shiftRightChain(sofaModule);
		this.setOrder();
		this.renderer.modulesChangeSideEffects();
	}

	async addFromData(moduleData: SerializedSofaModule) {
		let model = this.assetsLib.models.get(moduleData.asset.model!.path);
		if (!model)
			model = await this.assetsLib.loadModel(moduleData.asset.model!.path);

		const sofaModule = new SofaModule(
			this.renderer,
			moduleData.asset,
			moduleData.id,
			model.clone(true)
		);
		await sofaModule.createConnectingSlots(moduleData);

		sofaModule.group.position.fromArray(moduleData.objectData.position);
		sofaModule.group.rotation.fromArray(moduleData.objectData.rotation);

		for (const targetName in moduleData.materialActions) {
			const action = moduleData.materialActions[targetName];
			this.renderer.actionHandler.dispatchAction(action, sofaModule);
		}

		this.addModuleToCollection(sofaModule);

		return sofaModule;
	}

	getAllSlots() {
		const slots: ConnectingSlot[] = [];

		this.sofaModulesMap.forEach((sofaModule) => {
			slots.push(...sofaModule.connectingSlots);
		});

		return slots;
	}

	addModuleToCollection(sofaModule: SofaModule) {
		this.sofaModulesMap.set(sofaModule.id, sofaModule);
		this.renderer.collection.add(sofaModule.group);
	}

	removeSofaModule(creatorModule: CreatorProductModule) {
		const sofaModule = this.sofaModulesMap.get(creatorModule.id);
		if (!sofaModule) return;

		sofaModule.dispose();
		this.sofaModulesMap.delete(sofaModule.id);
		this.setOrder();
	}

	clearCollection() {
		this.sofaModulesMap.forEach((sofaModule) => {
			sofaModule.dispose();
		});

		this.sofaModulesMap.clear();
	}

	export(): SerializedModulesManager {
		return {
			sofaModules: [...this.sofaModulesMap.values()].map((sofaModule) =>
				sofaModule.export()
			),
		};
	}
}
